import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('checking'); // checking, valid, already-verified, expired, invalid
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Check token validity on mount
  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      setError('No verification link provided');
      return;
    }

    checkTokenStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const checkTokenStatus = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/email/verify-status?token=${encodeURIComponent(token)}`, {
        method: 'GET',
      });

      const data = await response.json();

      if (data.valid && data.alreadyVerified) {
        setStatus('already-verified');
      } else if (data.valid) {
        setStatus('valid');
      } else if (data.expired) {
        setStatus('expired');
      } else {
        setStatus('invalid');
      }
    } catch (err) {
      console.error('Failed to check token status:', err);
      setStatus('error');
      setError('Failed to validate verification link');
    }
  };

  const handleVerify = async () => {
    if (loading) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/email/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      const contentType = response.headers.get('content-type');
      const data = contentType?.includes('application/json')
        ? await response.json()
        : null;

      if (!response.ok) {
        setStatus('error');
        setError(data?.message || `Request failed (${response.status}). Please try again.`);
        return;
      }

      if (data.success) {
        setStatus('verified');
        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else if (data.already_verified) {
        setStatus('already-verified');
      } else if (data.message?.includes('expired')) {
        setStatus('expired');
        setError(data.message);
      } else {
        setStatus('error');
        setError(data.message || 'Failed to verify email');
      }
    } catch (err) {
      console.error('Verification failed:', err);
      setStatus('error');
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendClick = () => {
    navigate('/signup?resend=true');
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-margin-mobile pt-20">
      <div className="w-full max-w-md">
        <h1 className="text-headline-lg text-on-background mb-2 text-center">Verify Your Email</h1>

        {status === 'checking' && (
          <div className="text-center">
            <p className="text-body-md text-on-background/60">Checking verification link...</p>
            <div className="mt-8 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          </div>
        )}

        {status === 'valid' && (
          <div>
            <p className="text-body-md text-on-background/60 mb-6 text-center">
              Click the button below to verify your email and activate your account.
            </p>
            <button
              onClick={handleVerify}
              disabled={loading}
              className="w-full bg-primary text-on-primary font-medium py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 text-body-md"
            >
              {loading ? 'Verifying...' : 'Verify Email'}
            </button>
          </div>
        )}

        {status === 'verified' && (
          <div className="text-center">
            <div className="mb-4 text-4xl">✓</div>
            <p className="text-body-md text-on-background mb-4">
              Email verified! Redirecting to login...
            </p>
          </div>
        )}

        {status === 'already-verified' && (
          <div className="text-center">
            <p className="text-body-md text-on-background/60 mb-6">
              Your email has already been verified.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-primary text-on-primary font-medium py-2 rounded-lg hover:opacity-90 transition-opacity text-body-md"
            >
              Go to Login
            </button>
          </div>
        )}

        {status === 'expired' && (
          <div className="text-center">
            <p className="text-body-md text-error mb-6">
              This verification link has expired (24 hours max).
            </p>
            <button
              onClick={handleResendClick}
              className="w-full bg-primary text-on-primary font-medium py-2 rounded-lg hover:opacity-90 transition-opacity text-body-md"
            >
              Request New Link
            </button>
          </div>
        )}

        {status === 'invalid' && (
          <div className="text-center">
            <p className="text-body-md text-error mb-6">
              This verification link is invalid or has already been used.
            </p>
            <button
              onClick={handleResendClick}
              className="w-full bg-primary text-on-primary font-medium py-2 rounded-lg hover:opacity-90 transition-opacity text-body-md"
            >
              Request New Link
            </button>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <p className="text-body-md text-error mb-2">{error}</p>
            <button
              onClick={handleResendClick}
              className="w-full bg-primary text-on-primary font-medium py-2 rounded-lg hover:opacity-90 transition-opacity text-body-md mt-4"
            >
              Try Again
            </button>
          </div>
        )}

        <p className="text-body-sm text-on-background/60 text-center mt-8">
          Already verified? <a href="/login" className="text-primary font-medium hover:underline">Log in</a>
        </p>
      </div>
    </div>
  );
}
